import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

import { apiClient } from "../api/client";
import { EmployeePosition } from "../api/PartnerPortalGatewayApi";
import { RootState } from "../store";

interface UseRecentlyWorkedEducatorsReturn {
  data: EmployeePosition[] | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export const useRecentlyWorkedEducators = (): UseRecentlyWorkedEducatorsReturn => {
  const selectedService = useSelector((state: RootState) => state.service.selectedService);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["recently-worked-educators", selectedService?.serviceId],
    queryFn: async () => {
      if (!selectedService?.serviceId) {
        throw new Error("No service selected");
      }

      // Calculate date range for the last 20 days (excluding current week)
      const currentWeekStart = dayjs().startOf("week").add(1, "day"); // Monday
      const endDate = currentWeekStart.subtract(1, "day"); // Previous Sunday
      const startDate = endDate.subtract(19, "days"); // 20 days back

      return await apiClient.educators.getServiceEmployeesWorkedInDateRange(
        selectedService.serviceId!,
        startDate,
        endDate,
      );
    },
    enabled: !!selectedService?.serviceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};
